import * as React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Text } from '@lightspeed/flame/Text';

import { ScanDataContext } from 'root/apps/InventoryCountScanQueue/context/ScanData';
import { useDebounced } from 'root/apps/InventoryCountScanQueue/useDebounced';
import { useOnlineStatus } from 'root/apps/InventoryCountScanQueue/useOnlineStatus';
import { readScanEvent, SCAN_EVENT } from 'root/scan/ScanEvent';

import ScanQueueModal from './ScanQueueModal';

interface ButtonProps {
  isOpen: boolean;
}

export const UPDATE_COLLECTIONS_DEBOUNCE_DELAY = 100;
export const SYNC_PENDING_DEBOUNCE_DELAY = 2000;

const refreshListing = () => {
  window.RefreshListing('inventory_count_listings_inventory_count_items_view');
};

const ScanQueueButton: React.FC<ButtonProps> = ({ isOpen }) => {
  const [isModalOpen, setIsModalOpen] = React.useState(isOpen);
  const isMounted = React.useRef<boolean>(true);

  const { addBarcodeScan, updateCollections, syncPending, purgeByStatus } =
    React.useContext(ScanDataContext);

  const isOnline = useOnlineStatus();

  const { triggerDebounce: triggerUpdateCollection } = useDebounced(updateCollections, {
    delay: UPDATE_COLLECTIONS_DEBOUNCE_DELAY,
    enableQueue: true,
    beginDelayBeforeResolve: false,
  });
  const { triggerDebounce: triggerSyncPending } = useDebounced(
    React.useCallback(async () => {
      await syncPending();
      if (!isMounted.current) return;
      triggerUpdateCollection();
    }, [syncPending, triggerUpdateCollection]),
    {
      delay: SYNC_PENDING_DEBOUNCE_DELAY,
      enableQueue: true,
      beginDelayBeforeResolve: true,
    }
  );

  React.useEffect(() => {
    const handleBarcodeScan = (event: Event) => {
      setIsModalOpen(true);
      const barcodeEvent = readScanEvent(event);
      if (!barcodeEvent || !barcodeEvent.detail.barcode) return;
      addBarcodeScan(barcodeEvent.detail.barcode);
      triggerUpdateCollection();
      triggerSyncPending();
    };

    window.removeEventListener(SCAN_EVENT, handleBarcodeScan);
    window.addEventListener(SCAN_EVENT, handleBarcodeScan);
    return () => {
      window.removeEventListener(SCAN_EVENT, handleBarcodeScan);
    };
  }, [addBarcodeScan, triggerSyncPending, triggerUpdateCollection]);

  React.useEffect(() => {
    if (isModalOpen && isOnline) {
      triggerSyncPending();
    }
  }, [isModalOpen, isOnline, triggerSyncPending]);

  const handleOnClick = () => {
    setIsModalOpen(true);
  };

  const handleOnClose = () => {
    setIsModalOpen(false);
    purgeByStatus();
    if (isOnline) refreshListing();
  };

  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Box p={1} data-test="scan-queue-button-container" className="dispatch-global-scan">
      <Flex justifyContent="space-between" alignItems="center">
        <Text>To scan, begin scanning your items or by clicking on the button.</Text>
        <Button
          height={1}
          p={0}
          ml={1}
          size="small"
          variant="secondary"
          fill
          onClick={handleOnClick}
          data-test="scan-queue-button"
          style={{ whiteSpace: 'nowrap' }}
        >
          Enter scan mode
        </Button>
      </Flex>
      <ScanQueueModal isOpen={isModalOpen} onClose={handleOnClose} />
    </Box>
  );
};

export default ScanQueueButton;
